import {
  BENEFICIARY_STATUS,
  isTransitionAllowed,
  getStatusRules,
  SUSPENSION_REASONS,
  ARCHIVE_REASONS
} from '../constants/beneficiaryStatuses';

/**
 * Utilitaires de validation pour les statuts des bénéficiaires
 */

/**
 * Valide qu'une raison de suspension est valide
 * @param {string} reason - Code de la raison
 * @param {string} customReason - Raison personnalisée si "autre"
 * @returns {object} { valid: boolean, error?: string, finalReason?: string }
 */
export const validateSuspensionReason = (reason, customReason = '') => {
  const validReasons = SUSPENSION_REASONS.map(r => r.value);

  if (!reason || !reason.trim()) {
    return {
      valid: false,
      error: 'La raison de suspension est obligatoire'
    };
  }

  if (!validReasons.includes(reason)) {
    return {
      valid: false,
      error: 'Raison de suspension invalide'
    };
  }

  // Si "autre", vérifier que customReason est fourni
  if (reason === 'autre') {
    if (!customReason || !customReason.trim()) {
      return {
        valid: false,
        error: 'Veuillez préciser la raison de suspension'
      };
    }

    if (customReason.trim().length < 10) {
      return {
        valid: false,
        error: 'La raison personnalisée doit contenir au moins 10 caractères'
      };
    }

    return {
      valid: true,
      finalReason: customReason.trim()
    };
  }

  return {
    valid: true,
    finalReason: reason
  };
};

/**
 * Valide qu'une raison d'archivage est valide
 * @param {string} reason - Code de la raison
 * @param {string} customReason - Raison personnalisée si "autre"
 * @returns {object} { valid: boolean, error?: string, finalReason?: string }
 */
export const validateArchiveReason = (reason, customReason = '') => {
  const validReasons = ARCHIVE_REASONS.map(r => r.value);

  if (!reason || !reason.trim()) {
    return {
      valid: false,
      error: 'La raison d\'archivage est obligatoire'
    };
  }

  if (!validReasons.includes(reason)) {
    return {
      valid: false,
      error: 'Raison d\'archivage invalide'
    };
  }

  // Si "autre", vérifier que customReason est fourni
  if (reason === 'autre') {
    if (!customReason || !customReason.trim()) {
      return {
        valid: false,
        error: 'Veuillez préciser la raison d\'archivage'
      };
    }

    if (customReason.trim().length < 10) {
      return {
        valid: false,
        error: 'La raison personnalisée doit contenir au moins 10 caractères'
      };
    }

    return {
      valid: true,
      finalReason: customReason.trim()
    };
  }

  return {
    valid: true,
    finalReason: reason
  };
};

/**
 * Valide une date de fin de suspension
 * @param {Date|string} endDate - Date de fin
 * @returns {object} { valid: boolean, error?: string, parsedDate?: Date }
 */
export const validateSuspensionEndDate = (endDate) => {
  if (!endDate) {
    return {
      valid: false,
      error: 'La date de fin de suspension est obligatoire'
    };
  }

  const parsedDate = endDate instanceof Date ? endDate : new Date(endDate);

  if (isNaN(parsedDate.getTime())) {
    return {
      valid: false,
      error: 'Date de fin invalide'
    };
  }

  const now = new Date();
  now.setHours(0, 0, 0, 0); // Début de la journée

  if (parsedDate <= now) {
    return {
      valid: false,
      error: 'La date de fin doit être dans le futur'
    };
  }

  // Vérifier que la date n'est pas trop loin dans le futur (max 2 ans)
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 2);

  if (parsedDate > maxDate) {
    return {
      valid: false,
      error: 'La date de fin ne peut pas dépasser 2 ans'
    };
  }

  return {
    valid: true,
    parsedDate
  };
};

/**
 * Valide une date d'archivage
 * @param {Date|string} archiveDate - Date d'archivage
 * @returns {object} { valid: boolean, error?: string, parsedDate?: Date }
 */
export const validateArchiveDate = (archiveDate) => {
  if (!archiveDate) {
    return {
      valid: false,
      error: 'La date d\'archivage est obligatoire'
    };
  }

  const parsedDate = archiveDate instanceof Date ? archiveDate : new Date(archiveDate);

  if (isNaN(parsedDate.getTime())) {
    return {
      valid: false,
      error: 'Date d\'archivage invalide'
    };
  }

  const now = new Date();
  now.setHours(23, 59, 59, 999); // Fin de la journée

  if (parsedDate > now) {
    return {
      valid: false,
      error: 'La date d\'archivage ne peut pas être dans le futur'
    };
  }

  // Vérifier que la date n'est pas trop ancienne (max 10 ans)
  const minDate = new Date();
  minDate.setFullYear(minDate.getFullYear() - 10);

  if (parsedDate < minDate) {
    return {
      valid: false,
      error: 'La date d\'archivage ne peut pas dépasser 10 ans dans le passé'
    };
  }

  return {
    valid: true,
    parsedDate
  };
};

/**
 * Valide une transition de statut
 * @param {string} currentStatus - Statut actuel
 * @param {string} newStatus - Nouveau statut
 * @returns {object} { valid: boolean, error?: string }
 */
export const validateStatusTransition = (currentStatus, newStatus) => {
  if (!currentStatus || !newStatus) {
    return {
      valid: false,
      error: 'Statut actuel ou nouveau statut manquant'
    };
  }

  if (!Object.values(BENEFICIARY_STATUS).includes(currentStatus)) {
    return {
      valid: false,
      error: `Statut actuel invalide: ${currentStatus}`
    };
  }

  if (!Object.values(BENEFICIARY_STATUS).includes(newStatus)) {
    return {
      valid: false,
      error: `Nouveau statut invalide: ${newStatus}`
    };
  }

  // EXCEPTION TEMPORAIRE: Permettre archivé → archivé pour migration
  if (currentStatus === newStatus) {
    // Autoriser seulement pour le statut "archivé" (pour migration)
    if (currentStatus === BENEFICIARY_STATUS.ARCHIVED && newStatus === BENEFICIARY_STATUS.ARCHIVED) {
      return { valid: true }; // Permettre de ré-archiver pour mise à jour
    }

    return {
      valid: false,
      error: 'Le nouveau statut doit être différent du statut actuel'
    };
  }

  if (!isTransitionAllowed(currentStatus, newStatus)) {
    return {
      valid: false,
      error: `Transition non autorisée de "${currentStatus}" vers "${newStatus}"`
    };
  }

  return { valid: true };
};

/**
 * Valide les données complètes pour un changement de statut
 * @param {string} currentStatus - Statut actuel
 * @param {string} newStatus - Nouveau statut
 * @param {object} data - Données du changement
 * @returns {object} { valid: boolean, errors: Array<string> }
 */
export const validateStatusChange = (currentStatus, newStatus, data = {}) => {
  const errors = [];

  // Validation de la transition
  const transitionValidation = validateStatusTransition(currentStatus, newStatus);
  if (!transitionValidation.valid) {
    errors.push(transitionValidation.error);
  }

  // Validations spécifiques par statut cible
  switch (newStatus) {
    case BENEFICIARY_STATUS.SUSPENDED:
      // Raison obligatoire
      const reasonValidation = validateSuspensionReason(data.reason, data.customReason);
      if (!reasonValidation.valid) {
        errors.push(reasonValidation.error);
      }

      // Date de fin obligatoire
      const dateValidation = validateSuspensionEndDate(data.endDate);
      if (!dateValidation.valid) {
        errors.push(dateValidation.error);
      }
      break;

    case BENEFICIARY_STATUS.ARCHIVED:
      // Raison obligatoire
      const archiveReasonValidation = validateArchiveReason(data.reason, data.customReason);
      if (!archiveReasonValidation.valid) {
        errors.push(archiveReasonValidation.error);
      }

      // Date d'archivage obligatoire
      const archiveDateValidation = validateArchiveDate(data.archiveDate);
      if (!archiveDateValidation.valid) {
        errors.push(archiveDateValidation.error);
      }
      break;

    case BENEFICIARY_STATUS.ACTIVE:
    case BENEFICIARY_STATUS.INACTIVE:
    case BENEFICIARY_STATUS.PENDING:
      // Pas de validation spécifique pour ces statuts
      break;
  }

  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Obtient un message d'avertissement pour un changement de statut
 * @param {string} currentStatus - Statut actuel
 * @param {string} newStatus - Nouveau statut
 * @param {object} beneficiary - Données du bénéficiaire
 * @returns {string|null} Message d'avertissement ou null
 */
export const getStatusChangeWarning = (currentStatus, newStatus, beneficiary = {}) => {
  const warnings = [];

  // Avertissement pour archivage
  if (newStatus === BENEFICIARY_STATUS.ARCHIVED) {
    warnings.push('⚠️ L\'archivage est une action définitive. Le bénéficiaire ne pourra plus être réactivé.');

    // Vérifier s'il y a des rendez-vous futurs
    if (beneficiary.hasUpcomingAppointments) {
      warnings.push('📅 Les rendez-vous futurs seront supprimés définitivement.');
    }

    // Vérifier s'il y a des factures impayées
    if (beneficiary.hasUnpaidInvoices) {
      warnings.push('💰 Des factures impayées existent pour ce bénéficiaire.');
    }
  }

  // Avertissement pour suspension
  if (newStatus === BENEFICIARY_STATUS.SUSPENDED) {
    if (beneficiary.hasUpcomingAppointments) {
      warnings.push('📅 Les rendez-vous futurs seront annulés.');
    }
  }

  // Avertissement pour passage en inactif
  if (newStatus === BENEFICIARY_STATUS.INACTIVE) {
    warnings.push('ℹ️ Un bénéficiaire inactif doit être relancé régulièrement.');
  }

  return warnings.length > 0 ? warnings.join('\n') : null;
};

/**
 * Vérifie si un utilisateur a les permissions pour changer le statut
 * @param {Function} hasPermission - Fonction de vérification des permissions
 * @returns {boolean}
 */
export const canChangeStatus = (hasPermission) => {
  if (!hasPermission || typeof hasPermission !== 'function') {
    return false;
  }

  return hasPermission({ resource: 'beneficiaries', action: 'manage' });
};

/**
 * Formate une durée pour l'affichage
 * @param {Date} startDate - Date de début
 * @param {Date} endDate - Date de fin (optionnelle)
 * @returns {string} Durée formatée
 */
export const formatStatusDuration = (startDate, endDate = null) => {
  const start = startDate instanceof Date ? startDate : new Date(startDate);
  const end = endDate ? (endDate instanceof Date ? endDate : new Date(endDate)) : new Date();

  const diffMs = end - start;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'Aujourd\'hui';
  } else if (diffDays === 1) {
    return '1 jour';
  } else if (diffDays < 7) {
    return `${diffDays} jours`;
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} semaine${weeks > 1 ? 's' : ''}`;
  } else if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months} mois`;
  } else {
    const years = Math.floor(diffDays / 365);
    const remainingMonths = Math.floor((diffDays % 365) / 30);
    if (remainingMonths > 0) {
      return `${years} an${years > 1 ? 's' : ''} et ${remainingMonths} mois`;
    }
    return `${years} an${years > 1 ? 's' : ''}`;
  }
};

/**
 * Calcule le nombre de jours jusqu'à une date
 * @param {Date|string} targetDate - Date cible
 * @returns {number} Nombre de jours (négatif si dans le passé)
 */
export const daysUntil = (targetDate) => {
  const target = targetDate instanceof Date ? targetDate : new Date(targetDate);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);

  const diffMs = target - now;
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
};

/**
 * Vérifie si une suspension est proche de sa fin
 * @param {object} suspensionDetails - Détails de la suspension
 * @param {number} daysThreshold - Nombre de jours de seuil (défaut: 7)
 * @returns {boolean}
 */
export const isSuspensionEndingSoon = (suspensionDetails, daysThreshold = 7) => {
  if (!suspensionDetails || !suspensionDetails.endDate) {
    return false;
  }

  const endDate = suspensionDetails.endDate.toDate ? suspensionDetails.endDate.toDate() : new Date(suspensionDetails.endDate);
  const days = daysUntil(endDate);

  return days >= 0 && days <= daysThreshold;
};

/**
 * Vérifie si une suspension est expirée
 * @param {object} suspensionDetails - Détails de la suspension
 * @returns {boolean}
 */
export const isSuspensionExpired = (suspensionDetails) => {
  if (!suspensionDetails || !suspensionDetails.endDate) {
    return false;
  }

  const endDate = suspensionDetails.endDate.toDate ? suspensionDetails.endDate.toDate() : new Date(suspensionDetails.endDate);
  return daysUntil(endDate) < 0;
};

/**
 * Vérifie si un bénéficiaire est inactif depuis longtemps
 * @param {object} inactiveDetails - Détails de l'inactivité
 * @param {number} daysThreshold - Nombre de jours de seuil (défaut: 30)
 * @returns {boolean}
 */
export const isInactiveLongTerm = (inactiveDetails, daysThreshold = 30) => {
  if (!inactiveDetails || !inactiveDetails.startDate) {
    return false;
  }

  const startDate = inactiveDetails.startDate.toDate ? inactiveDetails.startDate.toDate() : new Date(inactiveDetails.startDate);
  const days = Math.abs(daysUntil(startDate));

  return days >= daysThreshold;
};

export default {
  validateSuspensionReason,
  validateArchiveReason,
  validateSuspensionEndDate,
  validateArchiveDate,
  validateStatusTransition,
  validateStatusChange,
  getStatusChangeWarning,
  canChangeStatus,
  formatStatusDuration,
  daysUntil,
  isSuspensionEndingSoon,
  isSuspensionExpired,
  isInactiveLongTerm
};
